import React, { useState, useEffect, useCallback } from 'react';
import { DoorOpen, Download, Filter } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import { formatDateTime } from '../utils/datetime';
import gateSystemsService from '../services/gateSystemsService';
import { downloadBlob } from '../utils/reportExports';

// Gate Activity Log — the ESS-style access-event report.
// Columns: Date/time, Event, Gate, Customer Name, PIN/Card Number.
// Filters: date range + customer (substring) + event type. Export CSV.
//
// Events are ingested from the on-site gate bridge (provider-agnostic) and
// joined to a customer by gate code server-side. This page is the read
// surface. Phase 7 (Intelligence Moonshots) per the dashboard roadmap.

const EVENT_TYPES = [
  { value: 'granted', label: 'Access granted' },
  { value: 'denied', label: 'Access denied' },
  { value: 'open', label: 'Open' },
  { value: 'close', label: 'Close' },
  { value: 'unknown', label: 'Unknown access attempt' },
];

const EVENT_LABEL = EVENT_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

const EVENT_BADGE = {
  granted: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  denied: 'bg-red-50 text-red-700 ring-red-200',
  open: 'bg-blue-50 text-blue-700 ring-blue-200',
  close: 'bg-slate-50 text-slate-600 ring-slate-200',
  unknown: 'bg-amber-50 text-amber-700 ring-amber-200',
};

const GateActivityLog = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const timeZone = currentFacility?.timezone || undefined;

  const [filters, setFilters] = useState({ from: '', to: '', customer: '' });
  const [selectedTypes, setSelectedTypes] = useState([]); // empty = all
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback(() => {
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.customer.trim()) params.customer = filters.customer.trim();
    if (selectedTypes.length) params.event_types = selectedTypes.join(',');
    return params;
  }, [filters, selectedTypes]);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError('');
    try {
      const res = await gateSystemsService.listEvents(facilityId, buildParams());
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load gate activity.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [facilityId, buildParams]);

  useEffect(() => {
    load();
    // Intentionally only re-run on facility change — filters re-apply via the
    // Apply button, not on every keystroke (load() changes with each filter).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const toggleType = (value) => {
    setSelectedTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value],
    );
  };

  const onExportCsv = async () => {
    if (!facilityId) return;
    setExporting(true);
    try {
      const res = await gateSystemsService.exportEventsCsv(facilityId, buildParams());
      downloadBlob('gate_activity_log.csv', res.data, 'text/csv');
    } catch {
      setError('CSV export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (facilityLoading) {
    return <div className="text-[13px] text-slate-400">Loading…</div>;
  }
  if (!facilityId) {
    return (
      <div className="text-[13px] text-slate-400">
        Select a facility to view gate activity.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <DoorOpen className="h-5 w-5 text-slate-400" />
          Gate Activity Log
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Access events for {currentFacility?.name || 'this facility'}.
        </p>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200/80 bg-white p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              From
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px]"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              To
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px]"
            />
          </div>
          <div className="min-w-[180px]">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Customer
            </label>
            <input
              type="text"
              placeholder="Name contains…"
              value={filters.customer}
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[12px]"
            />
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-medium"
          >
            <Filter className="h-3.5 w-3.5" /> Apply
          </button>
          <button
            onClick={onExportCsv}
            disabled={exporting || !rows.length}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-[12px] font-medium disabled:text-white disabled:cursor-not-allowed"
            title="Download as CSV"
          >
            <Download className="h-3.5 w-3.5" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        {/* Event-type chips */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-[11px] text-slate-400 mr-1">Event:</span>
          {EVENT_TYPES.map((t) => {
            const active = selectedTypes.includes(t.value);
            return (
              <button
                key={t.value}
                onClick={() => toggleType(t.value)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium ring-1 transition-colors ${
                  active
                    ? 'bg-emerald-600 text-white ring-emerald-600'
                    : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
                }`}
                type="button"
              >
                {t.label}
              </button>
            );
          })}
          {selectedTypes.length > 0 && (
            <button
              onClick={() => setSelectedTypes([])}
              className="text-[11px] text-slate-400 hover:text-slate-600 underline"
              type="button"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        {loading ? (
          <p className="text-[13px] text-slate-400 py-10 text-center">Loading…</p>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Date/time</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Event</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Gate</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Customer Name</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">PIN/Card Number</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t border-slate-100 text-slate-700">
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[12px]">
                      {formatDateTime(row.occurred_at, timeZone)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ring-1 ${
                          EVENT_BADGE[row.event_type] || EVENT_BADGE.unknown
                        }`}
                      >
                        {EVENT_LABEL[row.event_type] || row.event_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.gate || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.customer_name || (
                        <span className="text-slate-400">Unmatched</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[12px]">
                      {row.raw_code || row.raw_credential_id || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-[13px] text-slate-400 py-10 text-center">
            No gate events for these filters.
          </p>
        )}
      </div>
    </div>
  );
};

export default GateActivityLog;
